import React, {useEffect, useState} from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {FieldGroup, FieldLabel, StyledDialogActions, StyledDialogContent} from './CronConfigDialog.styled';
import {ReactComponent as InfoSquareIcon} from "../../../assets/icons/info_square.svg";
import {ActionButton, CancelButton} from '../../common/ActionButtons';
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from '../../common/FieldHelpSuffix';
import {validateCronField} from "../../../utils/cron-utils";

interface CronConfigDialogProps {
    open: boolean;
    currentCron: string;
    onClose: () => void;
    onSave: (newCron: string) => void;
    mode?: 'edit' | 'create';
}

type ERROR_KEY = 'seconds' | 'minutes' | 'hours' | 'dayOfMonth' | 'month' | 'dayOfWeek';

const CronConfigDialog = ({open, currentCron, onClose, onSave, mode = 'edit'}: CronConfigDialogProps) => {
    const {t} = useTranslation();

    const [seconds, setSeconds] = useState<string>('0');
    const [minutes, setMinutes] = useState<string>('0');
    const [hours, setHours] = useState<string>('0');
    const [dayOfMonth, setDayOfMonth] = useState<string>('*');
    const [month, setMonth] = useState<string>('*');
    const [dayOfWeek, setDayOfWeek] = useState<string>('?');

    const [errors, setErrors] = useState<Partial<Record<ERROR_KEY, string>>>({});

    useEffect(() => {
        if (!open) return;

        setErrors({});

        if (open && currentCron) {
            const parts = currentCron.split(' ');

            if (parts.length === 6) {
                setSeconds(parts[0]);
                setMinutes(parts[1]);
                setHours(parts[2]);
                setDayOfMonth(parts[3]);
                setMonth(parts[4]);
                setDayOfWeek(parts[5]);
            }
            else if (parts.length === 5) {
                setSeconds('0');
                setMinutes(parts[0]);
                setHours(parts[1]);
                setDayOfMonth(parts[2]);
                setMonth(parts[3]);
                setDayOfWeek(parts[4] === '*' && parts[2] !== '*' ? '?' : parts[4]);
            }
        } else if (open) {
            setSeconds('0');
            setMinutes('0');
            setHours('12');
            setDayOfMonth('*');
            setMonth('*');
            setDayOfWeek('?');
        }
    }, [open, currentCron]);

    const validate = (): boolean => {
        const newErrors: Partial<Record<ERROR_KEY, string>> = {};

        const secondsErr = validateCronField(seconds, 0, 59);
        if (secondsErr) newErrors.seconds = secondsErr;

        const minutesErr = validateCronField(minutes, 0, 59);
        if (minutesErr) newErrors.minutes = minutesErr;

        const hoursErr = validateCronField(hours, 0, 23);
        if (hoursErr) newErrors.hours = hoursErr;

        const domActive = dayOfMonth !== '*' && dayOfMonth !== '?';
        const dowActive = dayOfWeek !== '*' && dayOfWeek !== '?';
        const bothWildcard = dayOfMonth === '*' && dayOfWeek === '*';

        if (domActive && dowActive) {
            newErrors.dayOfMonth = t('cronConfig.validation.dayConflict') ?? '';
            newErrors.dayOfWeek = t('cronConfig.validation.dayConflict') ?? '';
        } else if (bothWildcard) {
            newErrors.dayOfMonth = t('cronConfig.validation.dayBothWildcard') ?? '';
            newErrors.dayOfWeek = t('cronConfig.validation.dayBothWildcard') ?? '';
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }

        let finalDayOfMonth = dayOfMonth;
        let finalDayOfWeek = dayOfWeek;

        if (finalDayOfMonth !== '*' && finalDayOfMonth !== '?') {
            finalDayOfWeek = '?';
        } else if (finalDayOfWeek !== '*' && finalDayOfWeek !== '?') {
            finalDayOfMonth = '?';
        } else if (finalDayOfMonth === '?' && finalDayOfWeek === '?') {
            finalDayOfMonth = '*';
            finalDayOfWeek = '?';
        }

        const newCron = `${seconds} ${minutes} ${hours} ${finalDayOfMonth} ${month} ${finalDayOfWeek}`;
        onSave(newCron);
        onClose();
    };

    const constructedCron = `${seconds} ${minutes} ${hours} ${dayOfMonth === '*' && dayOfWeek !== '*' && dayOfWeek !== '?' ? '?' : dayOfMonth} ${month} ${dayOfMonth !== '*' && dayOfMonth !== '?' ? '?' : dayOfWeek}`;


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {t('cronConfig.title')}

                <Tooltip title={t('cronConfig.explanation')}>
                    <IconButton size="small" sx={{color: 'primary.main'}} aria-label={t('cronConfig.explanation') as string}>
                        <InfoSquareIcon aria-hidden={true} focusable={false} style={{width: 24, height: 24}}/>
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <StyledDialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.second')}</FieldLabel>

                            <TextField
                                value={seconds}
                                onChange={(e) => {
                                    if (errors.seconds) setErrors(prev => ({ ...prev, seconds: '' }));
                                    setSeconds(e.target.value);
                                }}
                                error={!!errors.seconds}
                                helperText={errors.seconds ?? ""}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.seconds')}
                                            ariaLabel={t('cronConfig.help.seconds') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.minute')}</FieldLabel>

                            <TextField
                                value={minutes}
                                onChange={(e) => {
                                    if (errors.minutes) setErrors(prev => ({ ...prev, minutes: '' }));
                                    setMinutes(e.target.value);
                                }}
                                error={!!errors.minutes}
                                helperText={errors.minutes ?? ""}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.minutes')}
                                            ariaLabel={t('cronConfig.help.minutes') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.hour')}</FieldLabel>

                            <TextField
                                value={hours}
                                onChange={(e) => {
                                    if (errors.hours) setErrors(prev => ({ ...prev, hours: '' }));
                                    setHours(e.target.value);
                                }}
                                error={!!errors.hours}
                                helperText={errors.hours ?? ""}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.hours')}
                                            ariaLabel={t('cronConfig.help.hours') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.dayOfMonth')}</FieldLabel>

                            <FormControl fullWidth variant="outlined" error={!!errors.dayOfMonth}>
                                <Select
                                    value={dayOfMonth}
                                    onChange={(e) => {
                                        if (errors.dayOfMonth) setErrors(prev => ({ ...prev, dayOfWeek: '', dayOfMonth: '' }));
                                        setDayOfMonth(e.target.value);
                                    }}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.dayOfMonth')}
                                                    ariaLabel={t('cronConfig.help.dayOfMonth') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    <MenuItem value="?">{t('cronConfig.ignoredWhenDayOfWeek')}</MenuItem>

                                    {[...Array(31)].map((_, i) => (
                                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.dayOfMonth && (
                                    <FormHelperText error>
                                        {errors.dayOfMonth}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </FieldGroup>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.month')}</FieldLabel>

                            <FormControl fullWidth variant="outlined">
                                <Select
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.month')}
                                                    ariaLabel={t('cronConfig.help.month') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    {[...Array(12)].map((_, i) => (
                                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FieldGroup>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.dayOfWeek')}</FieldLabel>

                            <FormControl fullWidth variant="outlined" error={!!errors.dayOfWeek}>
                                <Select
                                    value={dayOfWeek}
                                    onChange={(e) => {
                                        if (errors.dayOfWeek) setErrors(prev => ({ ...prev, dayOfWeek: '', dayOfMonth: '' }));
                                        setDayOfWeek(e.target.value);
                                    }}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.dayOfWeek')}
                                                    ariaLabel={t('cronConfig.help.dayOfWeek') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    <MenuItem value="?">{t('cronConfig.ignoredWhenDayOfMonth')}</MenuItem>
                                    <MenuItem value="1">{t('cronConfig.monday')}</MenuItem>
                                    <MenuItem value="2">{t('cronConfig.tuesday')}</MenuItem>
                                    <MenuItem value="3">{t('cronConfig.wednesday')}</MenuItem>
                                    <MenuItem value="4">{t('cronConfig.thursday')}</MenuItem>
                                    <MenuItem value="5">{t('cronConfig.friday')}</MenuItem>
                                    <MenuItem value="6">{t('cronConfig.saturday')}</MenuItem>
                                    <MenuItem value="0">{t('cronConfig.sunday')}</MenuItem>
                                </Select>

                                {errors.dayOfWeek && (
                                    <FormHelperText error>
                                        {errors.dayOfWeek}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </FieldGroup>
                    </Grid>
                </Grid>

                <Box mt={4} textAlign="center">
                    <Typography variant="subtitle1" component="p" gutterBottom>
                        {t('cronConfig.cronExpression')}
                    </Typography>

                    <Typography variant="h6" component="p" sx={{fontFamily: 'monospace', fontWeight: 'bold'}}>
                        {constructedCron}
                    </Typography>
                </Box>
            </StyledDialogContent>

            <StyledDialogActions>
                <CancelButton onClick={onClose}>{t('common.cancel')}</CancelButton>

                <ActionButton onClick={handleSave}>
                    {mode === 'edit' ? t('common.save') : t('common.confirm')}
                </ActionButton>
            </StyledDialogActions>
        </Dialog>
    );
};

export default CronConfigDialog;
